import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, studentApi, apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { LastActivityBadge } from '@/components/LastActivityBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import ImportButton from '@/components/ImportButton';
import ReminderModal from '@/components/ReminderModal';
import ActivationQuestionnaireModal from '@/components/ActivationQuestionnaireModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ClickToCall, CallHistory } from '@/components/ClickToCall';
import { CallRecordings } from '@/components/CallRecordings';
import { TransactionHistory } from '@/components/TransactionHistory';
import { MentionTextarea, RenderNoteBody } from '@/components/MentionTextarea';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UpgradeConfirmPaymentModal from '@/components/UpgradeConfirmPaymentModal';
import { getCourseColor, UpgradeHistoryCard, UpgradePathIndicator } from '@/components/UpgradeModal';
import { PeriodFilter } from '@/components/PeriodFilter';
import { EnrollmentMonthFilter } from '@/components/EnrollmentMonthFilter';
import { Pagination } from '@/components/Pagination';
import MergeStudentModal from '@/components/MergeStudentModal';
import {
    Search,
    Phone,
    Mail,
    User,
    GraduationCap,
    CheckCircle,
    Clock,
    TrendingUp,
    Target,
    Package,
    ShoppingCart,
    MoreVertical,
    Bell,
    PhoneCall,
    GripVertical,
    ArrowUp,
    Download,
    DollarSign,
    GitMerge,
    Palette,
    Shield,
    Star,
    AlertTriangle,
    PhoneOff,
    BarChart3,
    RefreshCw,
    LayoutGrid,
    List,
    Loader2,
    Send,
    MessageSquare,
} from 'lucide-react';
import logger from '@/lib/logger';

const COLOR_TAGS = [
    { id: 'handle_with_care', label: 'Handle With Care', color: 'bg-amber-100 border-amber-400 text-amber-800', dot: 'bg-amber-500', icon: AlertTriangle },
    { id: 'do_not_disturb', label: 'Do Not Disturb', color: 'bg-red-100 border-red-400 text-red-800', dot: 'bg-red-500', icon: PhoneOff },
    { id: 'vip', label: 'VIP Client', color: 'bg-yellow-100 border-yellow-500 text-yellow-800', dot: 'bg-yellow-500', icon: Star },
    { id: 'priority', label: 'Priority', color: 'bg-purple-100 border-purple-400 text-purple-800', dot: 'bg-purple-500', icon: Shield },
    { id: 'follow_up', label: 'Follow Up', color: 'bg-blue-100 border-blue-400 text-blue-800', dot: 'bg-blue-500', icon: Bell },
];

const getColorTagStyle = (tag) => COLOR_TAGS.find(t => t.id === tag) || null;

const CS_STAGES = [
    { id: 'new_student', label: 'New Student', color: 'bg-blue-500', icon: User },
    { id: 'activated', label: 'Activated', color: 'bg-emerald-500', icon: CheckCircle },
    { id: 'satisfactory_call', label: 'Satisfactory Call', color: 'bg-purple-500', icon: Phone },
    { id: 'pitched_for_upgrade', label: 'Pitched Upgrade', color: 'bg-orange-500', icon: TrendingUp },
    { id: 'upgraded', label: 'Upgraded', color: 'bg-emerald-600', icon: ArrowUp },
];

const StudentCard = ({ student, onView, onSetReminder, onInitiateUpgrade, isDragging, isSuperAdmin, csAgents, onQuickReassign, onColorTag }) => {
    const [showReassign, setShowReassign] = React.useState(false);
    const hasReminder = student.reminder_date && !student.reminder_completed;
    const isUpgradedStudent = student.is_upgraded_student;
    const isShadow = student.is_shadow;
    const isNewImport = student.is_new_from_import;
    const courseLevel = student.course_level || '';
    const courseName = courseLevel || student.current_course_name || student.package_bought;
    const courseColors = getCourseColor(courseName);
    const nextLevel = courseColors.next;
    const colorTag = getColorTagStyle(student.color_tag);

    // Color tag takes priority for border/bg
    const cardBorder = colorTag
        ? `border-2 ${colorTag.color.split(' ')[1]}`
        : isNewImport
            ? 'border-2 border-cyan-400 dark:border-cyan-600 ring-1 ring-cyan-400/30'
            : isUpgradedStudent ? `${courseColors.border} border-2` : '';
    const cardBg = colorTag
        ? colorTag.color.split(' ')[0]
        : isNewImport
            ? 'bg-cyan-50/50 dark:bg-cyan-900/20'
            : isUpgradedStudent ? courseColors.bg : '';

    return (
        <div
            className={`kanban-card stage-${student.stage} animate-fade-in cursor-pointer ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary' : ''} ${cardBg} ${cardBorder}`}
            onClick={() => !isDragging && onView(student)}
            data-testid={`student-card-${student.id}`}
        >
            {/* Color tag indicator strip */}
            {colorTag && (
                <div className={`-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md ${colorTag.color}`} data-testid={`color-tag-${student.id}`}>
                    <colorTag.icon className="h-3 w-3" />
                    {colorTag.label}
                </div>
            )}
            {/* Shadow card indicator */}
            {isShadow && (
                <div className="-mx-3 -mt-3 mb-2 px-3 py-1 flex items-center gap-1.5 text-[10px] font-semibold rounded-t-md bg-emerald-100 text-emerald-700 border-b border-emerald-300" data-testid={`shadow-indicator-${student.id}`}>
                    <ArrowUp className="h-3 w-3" />
                    Upgraded &middot; AED {(student.amount || 0).toLocaleString()}
                </div>
            )}
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground">
                        <GripVertical className="h-4 w-4" />
                    </div>
                    <div className={`w-8 h-8 rounded-full ${isNewImport ? 'bg-cyan-500 text-white' : isUpgradedStudent ? courseColors.bg + ' ' + courseColors.text : 'bg-emerald-600 text-white'} flex items-center justify-center text-sm font-medium`}>
                        {student.full_name?.charAt(0) || '?'}
                    </div>
                    <div>
                        <div className="flex items-center gap-1">
                            <p className="font-medium text-sm">{student.full_name}</p>
                            {isNewImport && (
                                <Badge className="bg-cyan-500 text-white text-[10px] px-1 py-0">NEW</Badge>
                            )}
                            {isUpgradedStudent && (
                                <Badge className="bg-emerald-500 text-white text-[10px] px-1 py-0">
                                    <ArrowUp className="h-2 w-2 mr-0.5" />
                                    x{student.upgrade_count || 1}
                                </Badge>
                            )}
                        </div>
                        {student.cs_agent_name && (
                            <div className="relative">
                                {isSuperAdmin ? (
                                    <button
                                        className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1 hover:underline"
                                        onClick={(e) => { e.stopPropagation(); setShowReassign(!showReassign); }}
                                        data-testid={`cs-reassign-btn-${student.id}`}
                                    >
                                        Agent: {student.cs_agent_name}
                                    </button>
                                ) : (
                                    <p className="text-xs text-muted-foreground">
                                        Agent: {student.cs_agent_name}
                                    </p>
                                )}
                                {showReassign && isSuperAdmin && (
                                    <div className="absolute z-50 top-6 left-0 bg-popover border rounded-md shadow-lg p-1 min-w-[180px] max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                        {(csAgents || []).map(agent => (
                                            <button
                                                key={agent.id}
                                                className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-accent ${agent.id === student.cs_agent_id ? 'bg-accent font-medium' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); onQuickReassign(student, agent); setShowReassign(false); }}
                                                data-testid={`cs-reassign-agent-${agent.id}`}
                                            >
                                                {agent.full_name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(student); }}>
                            View Details
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSetReminder(student); }}>
                            <Bell className="h-4 w-4 mr-2" />
                            Set Reminder
                        </DropdownMenuItem>
                        {!isShadow && onColorTag && (
                            <>
                                <DropdownMenuSeparator />
                                {COLOR_TAGS.map(tag => (
                                    <DropdownMenuItem key={tag.id} onClick={(e) => { e.stopPropagation(); onColorTag(student, student.color_tag === tag.id ? null : tag.id); }}>
                                        <div className={`h-3 w-3 rounded-full mr-2 ${tag.dot}`} />
                                        {student.color_tag === tag.id ? `Remove ${tag.label}` : tag.label}
                                    </DropdownMenuItem>
                                ))}
                                {student.color_tag && (
                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onColorTag(student, null); }} className="text-muted-foreground">
                                        Clear Tag
                                    </DropdownMenuItem>
                                )}
                            </>
                        )}
                        {student.stage === 'activated' && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); onInitiateUpgrade(student); }}
                                    className="text-emerald-600"
                                >
                                    <TrendingUp className="h-4 w-4 mr-2" />
                                    Initiate Upgrade
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            
            <div className="space-y-2 text-sm">
                {isShadow ? (
                    <>
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <GraduationCap className="h-3 w-3" />
                            <span className="text-xs">{student.course_upgrade || student.upgrade_to_course}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <DollarSign className="h-3 w-3" />
                            <span className="font-mono text-xs">AED {(student.amount || 0).toLocaleString()}</span>
                        </div>
                        {student.date && (
                            <div className="text-xs text-muted-foreground">
                                {new Date(student.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="h-3 w-3" />
                    <span className="font-mono flex-1">{student.phone}</span>
                    <ClickToCall 
                        phoneNumber={student.phone} 
                        contactId={student.id} 
                        contactName={student.full_name}
                        size="sm"
                        className="h-6 w-6"
                    />
                </div>
                {student.student_code && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                        <GraduationCap className="h-3 w-3" />
                        <span className="font-mono text-xs" data-testid={`student-code-${student.id}`}>ID: {student.student_code}</span>
                    </div>
                )}
                {courseName && (
                    <div className="flex items-center gap-1.5">
                        <GraduationCap className="h-3 w-3 text-muted-foreground" />
                        <Badge className={`${courseColors.bg} ${courseColors.text} ${courseColors.border} border text-xs`}>
                            {courseColors.label || courseName}
                        </Badge>
                        {nextLevel && (
                            <span className="text-[10px] text-muted-foreground">
                                <ArrowUp className="inline h-2.5 w-2.5" /> {nextLevel}
                            </span>
                        )}
                    </div>
                )}
                    </>
                )}
            </div>
            
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-2 flex-wrap">
                    {student.ltv_total > 0 && (
                        <Badge variant="outline" className="text-xs font-mono border-sky-300 text-sky-700 dark:text-sky-300" data-testid={`ltv-${student.id}`}>
                            <DollarSign className="h-3 w-3 mr-0.5" />
                            AED {student.ltv_total?.toLocaleString() || 0}
                        </Badge>
                    )}
                    {student.txn_count > 0 && (
                        <Badge variant="outline" className="text-[10px] border-slate-300 text-muted-foreground" data-testid={`txn-count-${student.id}`}>
                            {student.txn_count} txn{student.txn_count !== 1 ? 's' : ''}
                        </Badge>
                    )}
                    {student.onboarding_complete && (
                        <Badge className="bg-emerald-500 text-white text-xs">Onboarded</Badge>
                    )}
                    {student.upgrade_eligible && !isUpgradedStudent && (
                        <Badge className="bg-yellow-500 text-white text-xs">Upgrade Ready</Badge>
                    )}
                    {hasReminder && (
                        <Badge className="bg-amber-500 text-white text-xs">
                            <Bell className="h-3 w-3 mr-1" />
                            {student.reminder_time || 'Set'}
                        </Badge>
                    )}
                    {student.last_upgrade_date && (
                        <Badge variant="outline" className="text-xs text-muted-foreground border-emerald-300" data-testid={`upgrade-date-${student.id}`}>
                            <ArrowUp className="h-3 w-3 mr-0.5 text-emerald-500" />
                            {new Date(student.last_upgrade_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </Badge>
                    )}
                </div>
                <span className="text-xs text-muted-foreground">
                    {student.classes_attended} classes
                </span>
            </div>
            {(student.mentor_name || student.bd_agent_name) && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-border flex-wrap">
                    {student.mentor_name && (
                        <span className="text-[10px] text-violet-600 dark:text-violet-400" data-testid={`mentor-tag-${student.id}`}>
                            Mentor: {student.mentor_name.split(' ')[0]}
                        </span>
                    )}
                    {student.bd_agent_name && (
                        <span className="text-[10px] text-sky-600 dark:text-sky-400" data-testid={`bd-tag-${student.id}`}>
                            BD: {student.bd_agent_name.split(' ')[0]}
                        </span>
                    )}
                </div>
            )}
            <div className="mt-1.5" data-testid={`last-activity-${student.id}`}>
                <LastActivityBadge at={student.last_activity} by={student.last_activity_by} />
            </div>
        </div>
    );
};

// Sortable wrapper for StudentCard
const SortableStudentCard = ({ student, onView, onSetReminder, onInitiateUpgrade, isSuperAdmin, csAgents, onQuickReassign, onColorTag }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: student.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <StudentCard
                student={student}
                onView={onView}
                onSetReminder={onSetReminder}
                onInitiateUpgrade={onInitiateUpgrade}
                isDragging={isDragging}
                isSuperAdmin={isSuperAdmin}
                csAgents={csAgents}
                onQuickReassign={onQuickReassign}
                onColorTag={onColorTag}
            />
        </div>
    );
};

const COLUMN_PAGE_SIZE = 50;

const KanbanColumn = ({ stage, baseParams, shadowCards, onView, onSetReminder, onInitiateUpgrade, isSuperAdmin, csAgents, onQuickReassign, onColorTag, stageTotal, onStudentsFetched }) => {
    const [colPage, setColPage] = React.useState(1);
    const [colStudents, setColStudents] = React.useState([]);
    const [colTotal, setColTotal] = React.useState(stageTotal || 0);
    const [colLoading, setColLoading] = React.useState(true);
    const StageIcon = stage.icon;

    // Fetch this column's data independently
    React.useEffect(() => {
        let cancelled = false;
        const fetchColumnData = async () => {
            setColLoading(true);
            try {
                const res = await studentApi.getAll({ ...baseParams, stage: stage.id, page: colPage, page_size: COLUMN_PAGE_SIZE });
                if (cancelled) return;
                const data = res.data;
                const items = data?.items || (Array.isArray(data) ? data : []);
                setColStudents(items);
                setColTotal(data?.total || items.length);
                if (onStudentsFetched) onStudentsFetched(stage.id, items);
            } catch {
                if (!cancelled) setColStudents([]);
            }
            if (!cancelled) setColLoading(false);
        };
        fetchColumnData();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colPage, baseParams?.cs_agent_id, stage.id]);

    // For the Upgraded column, merge in shadow cards on page 1
    const displayStudents = stage.id === 'upgraded' && shadowCards?.length > 0 && colPage === 1
        ? [...shadowCards, ...colStudents.filter(s => !s.is_shadow)]
        : colStudents;

    const totalForDisplay = stage.id === 'upgraded' && shadowCards?.length > 0
        ? colTotal + shadowCards.length
        : colTotal;
    const totalColPages = Math.ceil(colTotal / COLUMN_PAGE_SIZE);
    const studentIds = displayStudents.map(s => s.id);

    // Make column a drop target
    const { setNodeRef, isOver } = useDroppable({
        id: stage.id,
    });

    return (
        <div 
            ref={setNodeRef}
            className={`flex flex-col min-w-0 flex-1 bg-muted/50 rounded-xl p-3 ${isOver ? 'ring-2 ring-primary ring-offset-2' : ''}`} 
            data-testid={`cs-column-${stage.id}`}
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 280px)' }}
        >
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                <div className="flex items-center gap-1.5">
                    <StageIcon className={`h-4 w-4 ${stage.color.replace('bg-', 'text-')}`} />
                    <h3 className="font-semibold text-sm">{stage.label}</h3>
                </div>
                <Badge variant="secondary" className="text-xs">{totalForDisplay}</Badge>
            </div>
            
            {colLoading ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : (
                <ScrollArea className="flex-1">
                    <SortableContext items={studentIds} strategy={verticalListSortingStrategy}>
                        <div className="space-y-2 min-h-[80px] p-0.5">
                            {displayStudents.map((student) => (
                                <SortableStudentCard
                                    key={student.id}
                                    student={student}
                                    onView={onView}
                                    onSetReminder={onSetReminder}
                                    onInitiateUpgrade={onInitiateUpgrade}
                                    isSuperAdmin={isSuperAdmin}
                                    csAgents={csAgents}
                                    onQuickReassign={onQuickReassign}
                                    onColorTag={onColorTag}
                                />
                            ))}
                            {displayStudents.length === 0 && (
                                <div className={`text-center text-muted-foreground py-6 text-xs border-2 border-dashed rounded-lg transition-colors ${isOver ? 'border-primary bg-primary/5' : 'border-muted'}`}>
                                    Drop students here
                                </div>
                            )}
                        </div>
                    </SortableContext>
                </ScrollArea>
            )}
            
            {/* Per-column pagination */}
            {totalColPages > 1 && (
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
                    <button 
                        onClick={() => setColPage(p => Math.max(1, p - 1))} 
                        disabled={colPage <= 1}
                        className="text-xs px-2 py-1 rounded bg-background border hover:bg-accent disabled:opacity-40"
                        data-testid={`col-prev-${stage.id}`}
                    >
                        Prev
                    </button>
                    <span className="text-[10px] text-muted-foreground">
                        {(colPage - 1) * COLUMN_PAGE_SIZE + 1}-{Math.min(colPage * COLUMN_PAGE_SIZE, colTotal)} of {colTotal}
                    </span>
                    <button 
                        onClick={() => setColPage(p => Math.min(totalColPages, p + 1))} 
                        disabled={colPage >= totalColPages}
                        className="text-xs px-2 py-1 rounded bg-background border hover:bg-accent disabled:opacity-40"
                        data-testid={`col-next-${stage.id}`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

const CustomerServicePage = () => {
    const { user } = useAuth();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [teamAgents, setTeamAgents] = useState([]);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [showActivationModal, setShowActivationModal] = useState(false);
    const [pendingActivationStudent, setPendingActivationStudent] = useState(null);
    const [reminderStudent, setReminderStudent] = useState(null);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [updateData, setUpdateData] = useState({
        stage: '',
        notes: '',
        onboarding_complete: false,
        classes_attended: 0,
        full_name: '',
        email: '',
        phone: '',
        additional_phone: '',
    });
    
    // Summary bar state
    const [stageSummary, setStageSummary] = useState(null);
    // Shadow cards for Upgraded column
    const [shadowCards, setShadowCards] = useState([]);
    // LTV sort toggle: null (off), 'desc', 'asc'
    const [ltvSort, setLtvSort] = useState(null);
    
    // Upgrade modal state
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [upgradeStudent, setUpgradeStudent] = useState(null);
    // Merge modal state
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeStudent, setMergeStudent] = useState(null);
    // Upgrade pricing modal (pitched_for_upgrade) — replaced with inline course selection
    const [showPricingModal, setShowPricingModal] = useState(false);
    const [pricingStudent, setPricingStudent] = useState(null);
    const [pricingCourseId, setPricingCourseId] = useState('');
    const [pricingAddons, setPricingAddons] = useState([]);
    const [pricingSubmitting, setPricingSubmitting] = useState(false);
    // Course catalog for inline selection
    const [catalogCourses, setCatalogCourses] = useState([]);
    const [catalogAddons, setCatalogAddons] = useState([]);
    // Upgrade confirm + payment modal (upgraded)
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmStudent, setConfirmStudent] = useState(null);
    // Student notes state
    const [studentNotes, setStudentNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [highlightNoteId, setHighlightNoteId] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    // Activity timeline
    const [activityTimeline, setActivityTimeline] = useState([]);
    const [activityLoading, setActivityLoading] = useState(false);
    // Enrollment month filter
    const [enrollmentMonth, setEnrollmentMonth] = useState('all');
    const [csPeriodFilter, setCsPeriodFilter] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [mentorPartner, setMentorPartner] = useState(null);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor)
    );

    const isHeadOrAdmin = ['cs_head', 'super_admin', 'admin'].includes(user?.role);
    const isSuperAdmin = user?.role === 'super_admin';
    const [viewMode, setViewMode] = useState('my_work');
    const [csAgentsList, setCsAgentsList] = useState([]);
    const [filterCSAgent, setFilterCSAgent] = useState('all');
    const [displayMode, setDisplayMode] = useState('kanban'); // 'kanban' or 'table'
    const [tableStageFilter, setTableStageFilter] = useState('all'); // stage filter for table view

    // Fetch CS agents for super admin quick reassign & filtering
    useEffect(() => {
        if (isSuperAdmin || isHeadOrAdmin) {
            apiClient.get('/users?department=Customer Service').then(res => setCsAgentsList(res.data || [])).catch(() => {});
        }
    }, [isSuperAdmin, isHeadOrAdmin]);

    // Fetch Mentor partner info
    useEffect(() => {
        if (user?.id) {
            apiClient.get(`/cs-mentor/partner/${user.id}`)
                .then(res => { if (res.data?.partner) setMentorPartner(res.data); })
                .catch(() => {});
        }
    }, [user?.id]);

    // Fetch course catalog (CS-visible courses & addons)
    useEffect(() => {
        const fetchCatalog = async () => {
            try {
                const res = await apiClient.get('/course-catalog');
                const grouped = res.data?.grouped || {};
                setCatalogCourses(grouped.upgrades || []);
                setCatalogAddons(grouped.addons || []);
            } catch (e) {
                console.error('Failed to fetch course catalog:', e);
            }
        };
        fetchCatalog();
    }, []);

    // Track all visible students across columns for DnD
    const [kanbanStudentsMap, setKanbanStudentsMap] = useState({});
    const allKanbanStudents = React.useMemo(() => Object.values(kanbanStudentsMap).flat(), [kanbanStudentsMap]);

    const handleColumnStudentsFetched = React.useCallback((stageId, items) => {
        setKanbanStudentsMap(prev => ({ ...prev, [stageId]: items }));
    }, []);

    // Build baseParams for columns
    const kanbanBaseParams = React.useMemo(() => {
        const params = {};
        if (viewMode === 'my_work' && isHeadOrAdmin) {
            params.cs_agent_id = user.id;
        }
        if (filterCSAgent !== 'all') {
            params.cs_agent_id = filterCSAgent;
        }
        return params;
    }, [viewMode, isHeadOrAdmin, user?.id, filterCSAgent]);

    const fetchStudents = useCallback(async () => {
        try {
            setLoading(true);
            const baseParams = {};
            if (viewMode === 'my_work' && isHeadOrAdmin) {
                baseParams.cs_agent_id = user.id;
            }
            if (filterCSAgent !== 'all') {
                baseParams.cs_agent_id = filterCSAgent;
            }
            // Only apply period filter for Kanban/LTV views, not Table view
            if (csPeriodFilter && !searchTerm && displayMode !== 'table') {
                baseParams.date_from = csPeriodFilter.date_from;
                baseParams.date_to = csPeriodFilter.date_to;
                baseParams.date_field = 'upgrade_date';
            }
            // Enrollment month filter
            if (enrollmentMonth && enrollmentMonth !== 'all') {
                const [y, m] = enrollmentMonth.split('-');
                const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
                baseParams.date_from = `${enrollmentMonth}-01`;
                baseParams.date_to = `${enrollmentMonth}-${lastDay}`;
                baseParams.date_field = 'enrolled_at';
            }

            // For Kanban mode: columns fetch their own data independently
            if (!searchTerm && !csPeriodFilter && !ltvSort && displayMode === 'kanban' && enrollmentMonth === 'all') {
                // Just set loading false — columns handle their own fetching
                setLoading(false);
                return;
            } else {
                // Search, period filter, LTV sort, or Table mode: use single paginated query
                const params = { ...baseParams, search: searchTerm || undefined, page: currentPage, page_size: pageSize };
                if (ltvSort) {
                    params.sort_by = 'ltv';
                    params.sort_order = ltvSort;
                    params.page_size = 100;
                }
                // Apply stage filter in table mode
                if (displayMode === 'table' && tableStageFilter && tableStageFilter !== 'all') {
                    params.stage = tableStageFilter;
                }
                const response = await studentApi.getAll(params);
                const data = response.data;
                const items = data?.items || (Array.isArray(data) ? data : []);
                setStudents(items);
                setTotalRecords(data?.total || items.length);
                setTotalPages(data?.total_pages || 0);
                if (viewMode === 'team' && isHeadOrAdmin) {
                    const agentMap = {};
                    items.forEach(s => {
                        const key = s.cs_agent_id || 'unassigned';
                        if (!agentMap[key]) agentMap[key] = { id: key, name: s.cs_agent_name || 'Unassigned', count: 0 };
                        agentMap[key].count++;
                    });
                    setTeamAgents(Object.values(agentMap).sort((a, b) => b.count - a.count));
                }
            }
        } catch (error) {
            toast.error('Failed to fetch students');
            logger.error(error);
        } finally {
            setLoading(false);
        }
    }, [viewMode, isHeadOrAdmin, user?.id, filterCSAgent, csPeriodFilter, searchTerm, displayMode, tableStageFilter, currentPage, pageSize, ltvSort, enrollmentMonth]);

    const fetchStageSummary = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterCSAgent !== 'all') params.set('cs_agent_id', filterCSAgent);
            if (viewMode === 'my_work' && isHeadOrAdmin) params.set('cs_agent_id', user.id);
            if (csPeriodFilter) {
                params.set('date_from', csPeriodFilter.date_from);
                params.set('date_to', csPeriodFilter.date_to);
            }
            const res = await apiClient.get(`/students/stage-summary?${params.toString()}`);
            setStageSummary(res.data);
        } catch { /* silent */ }
    }, [filterCSAgent, viewMode, isHeadOrAdmin, user?.id, csPeriodFilter]);

    const fetchShadowCards = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterCSAgent !== 'all') params.set('cs_agent_id', filterCSAgent);
            if (viewMode === 'my_work' && isHeadOrAdmin) params.set('cs_agent_id', user.id);
            if (csPeriodFilter) {
                params.set('date_from', csPeriodFilter.date_from);
                params.set('date_to', csPeriodFilter.date_to);
            }
            params.set('page_size', '100');
            const res = await apiClient.get(`/students/upgrade-shadows?${params.toString()}`);
            setShadowCards(res.data?.items || []);
        } catch { setShadowCards([]); }
    }, [filterCSAgent, viewMode, isHeadOrAdmin, user?.id, csPeriodFilter]);

    // Main data fetch effect - must be after useCallback definitions
    useEffect(() => {
        fetchStudents();
        fetchStageSummary();
        fetchShadowCards();
    }, [fetchStudents, fetchStageSummary, fetchShadowCards]);

    const handleColorTag = async (student, tag) => {
        try {
            await apiClient.patch(`/students/${student.student_id || student.id}/color-tag`, { color_tag: tag });
            toast.success(tag ? `Tagged as "${COLOR_TAGS.find(t => t.id === tag)?.label}"` : 'Tag removed');
            fetchStudents();
            fetchShadowCards();
        } catch (e) {
            toast.error('Failed to set color tag');
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    // Trigger fetch when searchTerm changes (debounced) — reset to page 1
    useEffect(() => {
        const timer = setTimeout(() => {
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    const handleViewStudent = (student, opts = {}) => {
        setSelectedStudent(student);
        setUpdateData({
            stage: student.stage,
            notes: student.notes || '',
            onboarding_complete: student.onboarding_complete || false,
            classes_attended: student.classes_attended || 0,
            full_name: student.full_name || '',
            email: student.email || '',
            phone: student.phone || '',
            additional_phone: student.additional_phone || '',
        });
        setHighlightNoteId(opts.highlightNoteId || null);
        fetchStudentNotes(student.id);
        fetchActivityTimeline(student.id);
        setShowDetailModal(true);
    };

    // Deep-link: open student + highlight note when arriving from a mention notification
    useEffect(() => {
        const studentId = searchParams.get('student_id');
        const noteId = searchParams.get('note_id');
        if (!studentId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await apiClient.get(`/students/${studentId}`);
                if (!cancelled && res?.data) {
                    handleViewStudent(res.data, { highlightNoteId: noteId });
                    const clean = new URLSearchParams(searchParams);
                    clean.delete('student_id');
                    clean.delete('note_id');
                    setSearchParams(clean, { replace: true });
                }
            } catch {
                toast.error('Could not open referenced student');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!highlightNoteId || !studentNotes.length) return;
        const el = document.querySelector(`[data-note-id="${highlightNoteId}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('note-pulse-highlight');
            const t = setTimeout(() => {
                el.classList.remove('note-pulse-highlight');
                setHighlightNoteId(null);
            }, 3200);
            return () => clearTimeout(t);
        }
    }, [highlightNoteId, studentNotes]);

    const fetchStudentNotes = async (studentId) => {
        try {
            const res = await apiClient.get(`/students/${studentId}/notes`);
            setStudentNotes(Array.isArray(res.data) ? res.data : []);
        } catch { setStudentNotes([]); }
    };

    const fetchActivityTimeline = async (studentId) => {
        setActivityLoading(true);
        try {
            const res = await apiClient.get(`/students/${studentId}/activity-timeline`);
            setActivityTimeline(Array.isArray(res.data) ? res.data : []);
        } catch { setActivityTimeline([]); }
        setActivityLoading(false);
    };

    const handleAddNote = async () => {
        if (!newNote.trim() || !selectedStudent) return;
        setSavingNote(true);
        try {
            await apiClient.post(`/students/${selectedStudent.id}/notes`, { text: newNote, type: 'call_note' });
            setNewNote('');
            toast.success('Note saved');
            fetchStudentNotes(selectedStudent.id);
        } catch { toast.error('Failed to save note'); }
        setSavingNote(false);
    };

    const ROLE_LABELS = {
        cs_agent: 'CS', cs_head: 'CS Head', mentor: 'Mentor', master_of_academics: 'Mentor',
        master_of_academics_: 'Mentor', business_development: 'BD', bd_executive: 'BD',
        super_admin: 'Admin', admin: 'Admin', sales_executive: 'Sales', team_leader: 'TL',
        sales_manager: 'Sales Mgr',
    };

    const handleSetReminder = (student) => {
        setReminderStudent(student);
        setShowReminderModal(true);
    };

    const handleReminderSuccess = () => {
        setShowReminderModal(false);
        setReminderStudent(null);
        fetchStudents();
    };

    // Initiate upgrade flow for a student
    const handleInitiateUpgrade = (student) => {
        setUpgradeStudent(student);
        setShowUpgradeModal(true);
    };

    const handleQuickReassignCS = async (student, agent) => {
        try {
            await studentApi.update(student.id, {
                cs_agent_id: agent.id,
                cs_agent_name: agent.full_name,
            });
            toast.success(`Reassigned to ${agent.full_name}`);
            fetchStudents();
        } catch (e) {
            logger.error(e);
            toast.error('Failed to reassign');
        }
    };

    // When upgrade is completed
    const handleUpgradeComplete = (updatedStudent) => {
        setShowUpgradeModal(false);
        setUpgradeStudent(null);
        fetchStudents();
        toast.success(`Upgrade initiated for ${updatedStudent.full_name}`);
    };

    // Drag and drop handlers
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const activeStudentId = active.id;
        const activeStudent = students.find(s => s.id === activeStudentId);
        
        if (!activeStudent) return;

        // Determine target stage
        let targetStage = null;
        
        // Check if dropped over a column (stage id)
        const isColumnDrop = CS_STAGES.some(s => s.id === over.id);
        if (isColumnDrop) {
            targetStage = over.id;
        } else {
            // Dropped over another student - get that student's stage
            const overStudent = students.find(s => s.id === over.id);
            if (overStudent) {
                targetStage = overStudent.stage;
            }
        }

        // If dropped in a different stage, update the student
        if (targetStage && targetStage !== activeStudent.stage) {
            // Check if moving to "activated" stage - show questionnaire
            if (targetStage === 'activated' && (activeStudent.stage === 'new_student')) {
                setPendingActivationStudent(activeStudent);
                setShowActivationModal(true);
                return;
            }

            // Check if moving to "pitched_for_upgrade" - show course selection inline
            if (targetStage === 'pitched_for_upgrade') {
                setPricingStudent(activeStudent);
                setPricingCourseId('');
                setPricingAddons([]);
                setShowPricingModal(true);
                return;
            }

            // Check if moving to "upgraded" - show confirm + payment modal
            if (targetStage === 'upgraded') {
                if (activeStudent.stage !== 'pitched_for_upgrade') {
                    toast.error('Student must be in "Pitched Upgrade" stage before upgrading');
                    return;
                }
                setConfirmStudent(activeStudent);
                setShowConfirmModal(true);
                return;
            }
            
            try {
                await studentApi.update(activeStudentId, { stage: targetStage });
                toast.success(`Student moved to ${CS_STAGES.find(s => s.id === targetStage)?.label}`);
                fetchStudents();
            } catch (error) {
                toast.error('Failed to move student');
                logger.error(error);
            }
        }
    };

    // Handle activation questionnaire completion
    const handleActivationComplete = async (questionnaireData) => {
        if (!pendingActivationStudent) return;
        
        try {
            await studentApi.update(pendingActivationStudent.id, { 
                stage: 'activated',
                activation_questionnaire: questionnaireData,
                onboarding_complete: true,
                activated_at: new Date().toISOString(),
                activated_by: user?.id,
            });
            toast.success('Student activated successfully!');
            setShowActivationModal(false);
            setPendingActivationStudent(null);
            fetchStudents();
        } catch (error) {
            toast.error('Failed to activate student');
            logger.error(error);
        }
    };

    const handleUpdateStudent = async () => {
        if (!selectedStudent) return;
        
        // Check if changing to "activated" stage from "new_student"
        if (updateData.stage === 'activated' && selectedStudent.stage === 'new_student') {
            setShowDetailModal(false);
            setPendingActivationStudent(selectedStudent);
            setShowActivationModal(true);
            return;
        }

        // Check if changing to "pitched_for_upgrade" - show pricing modal
        if (updateData.stage === 'pitched_for_upgrade' && selectedStudent.stage !== 'pitched_for_upgrade') {
            setShowDetailModal(false);
            setPricingStudent(selectedStudent);
            setPricingCourseId('');
            setPricingAddons([]);
            setShowPricingModal(true);
            return;
        }

        // Check if changing to "upgraded" - show confirm + payment modal
        if (updateData.stage === 'upgraded' && selectedStudent.stage !== 'upgraded') {
            if (selectedStudent.stage !== 'pitched_for_upgrade') {
                toast.error('Student must be in "Pitched Upgrade" stage before upgrading');
                return;
            }
            setShowDetailModal(false);
            setConfirmStudent(selectedStudent);
            setShowConfirmModal(true);
            return;
        }
        
        try {
            // Only send fields that have actual values to avoid overwriting with empty strings
            const updates = {};
            if (updateData.stage) updates.stage = updateData.stage;
            if (updateData.notes !== undefined && updateData.notes !== '') updates.notes = updateData.notes;
            if (updateData.full_name) updates.full_name = updateData.full_name;
            if (updateData.email) updates.email = updateData.email;
            if (updateData.phone) updates.phone = updateData.phone;
            if (updateData.additional_phone !== undefined) updates.additional_phone = updateData.additional_phone;
            updates.onboarding_complete = updateData.onboarding_complete;
            updates.classes_attended = updateData.classes_attended;
            
            await studentApi.update(selectedStudent.id, updates);
            toast.success('Student updated successfully');
            setShowDetailModal(false);
            fetchStudents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update student');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-AE', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="space-y-6" data-testid="cs-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Customer Service</h1>
                        <p className="text-muted-foreground">Manage student onboarding and support</p>
                    </div>
                    {mentorPartner?.partner && (
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800" data-testid="mentor-partner-badge">
                            <GraduationCap className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Mentor Partner:</span>
                            <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">{mentorPartner.partner.name}</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search students..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="pl-9 w-64"
                            data-testid="search-students"
                        />
                    </div>
                    {isSuperAdmin && csAgentsList.length > 0 && (
                        <Select value={filterCSAgent} onValueChange={setFilterCSAgent} data-testid="filter-cs-agent">
                            <SelectTrigger className="w-[160px] h-9 text-xs" data-testid="filter-cs-agent-trigger">
                                <SelectValue placeholder="All Agents" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Agents</SelectItem>
                                {csAgentsList.map(a => <SelectItem key={a.id} value={a.id}>{a.full_name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    )}
                    <PeriodFilter
                        dateFields={[{ value: 'upgrade_date', label: 'Upgrade Date' }]}
                        onChange={setCsPeriodFilter}
                        defaultPeriod="this_month"
                    />
                    <EnrollmentMonthFilter value={enrollmentMonth} onChange={(v) => { setEnrollmentMonth(v); setCurrentPage(1); }} />
                    {['super_admin', 'admin', 'cs_head'].includes(user?.role) && (
                        <>
                            <ImportButton templateType="students_cs" title="Import Students" onSuccess={fetchStudents} />
                            <Button variant="outline" size="sm" data-testid="migrate-students-btn"
                                onClick={async () => {
                                    if (!window.confirm('This will move all activated students WITHOUT a completed activation questionnaire back to "New Student" stage. Continue?')) return;
                                    try {
                                        const res = await apiClient.post('/students/migrate-to-new-student');
                                        toast.success(res.data.message);
                                        fetchStudents();
                                    } catch (err) {
                                        toast.error('Migration failed: ' + (err.response?.data?.detail || err.message));
                                    }
                                }}>
                                <RefreshCw className="h-4 w-4 mr-1.5" />Migrate Historic Data
                            </Button>
                        </>
                    )}
                    {isSuperAdmin && (
                        <Button variant="outline" size="sm" data-testid="export-students-btn"
                            onClick={() => {
                                const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                                const token = sessionStorage.getItem('clt_token');
                                window.open(`${API_URL}/api/students/export/excel?token=${token}`, '_blank');
                            }}>
                            <Download className="h-4 w-4 mr-1.5" />Export All
                        </Button>
                    )}
                </div>
            </div>

            {/* View Mode Toggle for CS Head / Admin */}
            {isHeadOrAdmin && (
                <div className="flex items-center gap-3" data-testid="cs-view-toggle">
                    <div className="inline-flex rounded-lg border p-1 bg-muted/30">
                        <button
                            onClick={() => setViewMode('my_work')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'my_work' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="cs-view-my-work"
                        >
                            My Students
                        </button>
                        <button
                            onClick={() => setViewMode('team')}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'team' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid="cs-view-team"
                        >
                            Team Overview
                        </button>
                    </div>
                    {viewMode === 'team' && teamAgents.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {teamAgents.map(agent => (
                                <Badge key={agent.id} variant="secondary" className="text-xs">
                                    {agent.name}: {agent.count}
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Summary Status Bar */}
            {stageSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="cs-summary-bar">
                    {CS_STAGES.map(stage => {
                        const count = stageSummary.stage_counts?.[stage.id] || 0;
                        return (
                            <div key={stage.id} className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card">
                                <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                                <div className="min-w-0">
                                    <p className="text-[10px] text-muted-foreground truncate">{stage.label}</p>
                                    <p className="text-sm font-bold">{count}</p>
                                </div>
                            </div>
                        );
                    })}
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-card">
                        <BarChart3 className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                            <p className="text-[10px] text-muted-foreground">Total</p>
                            <p className="text-sm font-bold">{stageSummary.total_students}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-emerald-50 dark:bg-emerald-950/30">
                        <DollarSign className="h-4 w-4 text-emerald-600 shrink-0" />
                        <div className="min-w-0">
                            <p className="text-[10px] text-muted-foreground">Period Revenue</p>
                            <p className="text-sm font-bold text-emerald-600">AED {(stageSummary.period_revenue || 0).toLocaleString()}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setLtvSort(prev => prev === null ? 'desc' : prev === 'desc' ? 'asc' : null)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${ltvSort ? 'bg-indigo-50 border-indigo-300 dark:bg-indigo-950/30 ring-1 ring-indigo-400' : 'bg-card hover:bg-muted/50'}`}
                        data-testid="ltv-sort-toggle"
                    >
                        <TrendingUp className={`h-4 w-4 shrink-0 ${ltvSort ? 'text-indigo-600' : 'text-muted-foreground'}`} />
                        <div className="min-w-0">
                            <p className="text-[10px] text-muted-foreground">LTV Sort</p>
                            <p className={`text-sm font-bold ${ltvSort ? 'text-indigo-600' : 'text-muted-foreground'}`}>
                                {ltvSort === 'desc' ? 'High→Low' : ltvSort === 'asc' ? 'Low→High' : 'Off'}
                            </p>
                        </div>
                    </button>
                </div>
            )}

            {/* Kanban/Table Toggle + Display */}
            <div className="flex items-center justify-between">
                <div className="inline-flex rounded-lg border p-1 bg-muted/30" data-testid="cs-display-toggle">
                    <button
                        onClick={() => { setDisplayMode('kanban'); setLtvSort(null); }}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${displayMode === 'kanban' && !ltvSort ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid="cs-kanban-toggle"
                    >
                        <LayoutGrid className="h-3.5 w-3.5" />
                        Kanban
                    </button>
                    <button
                        onClick={() => { setDisplayMode('table'); setLtvSort(null); setCurrentPage(1); }}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${displayMode === 'table' && !ltvSort ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                        data-testid="cs-table-toggle"
                    >
                        <List className="h-3.5 w-3.5" />
                        Table
                    </button>
                </div>

                {/* Stage filter — visible in Table mode */}
                {(displayMode === 'table' || ltvSort) && (
                    <Select value={tableStageFilter} onValueChange={(v) => { setTableStageFilter(v); setCurrentPage(1); }}>
                        <SelectTrigger className="w-[180px] h-9 text-xs" data-testid="table-stage-filter">
                            <SelectValue placeholder="Filter by Stage" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Stages</SelectItem>
                            {CS_STAGES.map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                    <span className="flex items-center gap-2">
                                        <span className={`inline-block w-2 h-2 rounded-full ${s.color}`} />
                                        {s.label} {stageSummary?.stage_counts?.[s.id] != null ? `(${stageSummary.stage_counts[s.id]})` : ''}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : ltvSort || displayMode === 'table' ? (
                /* Table / LTV Sorted List */
                <div className="border rounded-lg overflow-hidden" data-testid="cs-table-view">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/50 border-b">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">#</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Student</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Phone</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Stage</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Course</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Enrollment</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground cursor-pointer select-none" onClick={() => setLtvSort(prev => !prev ? 'desc' : prev === 'desc' ? 'asc' : null)}>
                                    <span className="inline-flex items-center gap-1">
                                        LTV {ltvSort === 'desc' ? <ArrowUp className="h-3 w-3 rotate-180" /> : ltvSort === 'asc' ? <ArrowUp className="h-3 w-3" /> : null}
                                    </span>
                                </th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Tag</th>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">CS Agent</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {students.map((s, idx) => {
                                const stageObj = CS_STAGES.find(st => st.id === s.stage);
                                const colorTag = getColorTagStyle(s.color_tag);
                                return (
                                    <tr key={s.id} className="hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => handleViewStudent(s)} data-testid={`table-row-${s.id}`}>
                                        <td className="px-4 py-2.5 text-muted-foreground">{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td className="px-4 py-2.5 font-medium">{s.full_name}</td>
                                        <td className="px-4 py-2.5 text-muted-foreground">{s.phone}</td>
                                        <td className="px-4 py-2.5">
                                            {stageObj ? (
                                                <Badge className={`${stageObj.color} text-white text-[10px]`}>{stageObj.label}</Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[10px]">{s.stage}</Badge>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-muted-foreground text-xs">{s.course_level || s.current_course_name || s.package_bought || '—'}</td>
                                        <td className="px-4 py-2.5 text-right font-mono text-xs">AED {(s.enrollment_amount || 0).toLocaleString()}</td>
                                        <td className="px-4 py-2.5 text-right font-mono font-bold text-indigo-600">AED {(s.ltv || s.enrollment_amount || 0).toLocaleString()}</td>
                                        <td className="px-4 py-2.5">
                                            {colorTag && (
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${colorTag.color}`}>
                                                    <div className={`h-1.5 w-1.5 rounded-full ${colorTag.dot}`} />
                                                    {colorTag.label}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-xs text-muted-foreground">{s.cs_agent_name || '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {students.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">No students found</div>
                    )}
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <div className="flex gap-3" style={{ minHeight: 'calc(100vh - 280px)' }} data-testid="cs-kanban-board">
                        {CS_STAGES.map((stage) => (
                            <KanbanColumn
                                key={stage.id}
                                stage={stage}
                                baseParams={kanbanBaseParams}
                                shadowCards={stage.id === 'upgraded' ? shadowCards : []}
                                onView={handleViewStudent}
                                onSetReminder={handleSetReminder}
                                onInitiateUpgrade={handleInitiateUpgrade}
                                isSuperAdmin={isSuperAdmin}
                                csAgents={csAgentsList}
                                onQuickReassign={handleQuickReassignCS}
                                onColorTag={handleColorTag}
                                stageTotal={stageSummary?.stage_counts?.[stage.id] || 0}
                                onStudentsFetched={handleColumnStudentsFetched}
                            />
                        ))}
                    </div>
                    <DragOverlay>
                        {activeId ? (
                            <StudentCard
                                student={allKanbanStudents.find(s => s.id === activeId) || students.find(s => s.id === activeId)}
                                onView={() => {}}
                                onSetReminder={() => {}}
                                onInitiateUpgrade={() => {}}
                                isDragging={true}
                            />
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}

            {/* Pagination — only for table/search/LTV mode */}
            {!loading && students.length > 0 && (displayMode === 'table' || ltvSort || searchTerm) && (
                <Pagination
                    page={currentPage}
                    totalPages={totalPages}
                    total={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* Student Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-2xl max-h-[90vh]">
                    {selectedStudent && (
                        <>
                            {/* Compact Header - Always Visible */}
                            <div className="flex items-center gap-3 pb-3 border-b">
                                <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                                    {selectedStudent.full_name?.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-semibold truncate">{selectedStudent.full_name}</h3>
                                        <Badge className={`text-[10px] ${CS_STAGES.find(s => s.id === selectedStudent.stage)?.color}`}>
                                            {CS_STAGES.find(s => s.id === selectedStudent.stage)?.label}
                                        </Badge>
                                        {selectedStudent.onboarding_complete && <Badge className="bg-emerald-500 text-[10px]">Onboarded</Badge>}
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{selectedStudent.phone}</span>
                                        {selectedStudent.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{selectedStudent.email}</span>}
                                        <span>{selectedStudent.package_bought || 'N/A'}</span>
                                    </div>
                                </div>
                                <ClickToCall 
                                    phoneNumber={selectedStudent.phone} 
                                    contactId={selectedStudent.id} 
                                    contactName={selectedStudent.full_name}
                                    variant="outline"
                                    size="sm"
                                    showLabel={false}
                                />
                                <Button variant="outline" size="sm" className="text-xs gap-1" data-testid="merge-student-btn"
                                    onClick={() => { setMergeStudent(selectedStudent); setShowMergeModal(true); }}>
                                    <GitMerge className="h-3 w-3" /> Merge
                                </Button>
                            </div>

                            {/* Tabbed Content */}
                            <Tabs defaultValue="info" className="mt-2">
                                <TabsList className="grid w-full grid-cols-6 h-8">
                                    <TabsTrigger value="info" className="text-xs" data-testid="tab-info">Info</TabsTrigger>
                                    <TabsTrigger value="notes" className="text-xs" data-testid="tab-notes">
                                        <MessageSquare className="h-3 w-3 mr-1" />Notes
                                    </TabsTrigger>
                                    <TabsTrigger value="activity" className="text-xs" data-testid="tab-activity">Activity</TabsTrigger>
                                    <TabsTrigger value="transactions" className="text-xs" data-testid="tab-transactions">
                                        <DollarSign className="h-3 w-3 mr-1" />Txns
                                    </TabsTrigger>
                                    <TabsTrigger value="calls" className="text-xs" data-testid="tab-calls">
                                        <PhoneCall className="h-3 w-3 mr-1" />Calls
                                    </TabsTrigger>
                                    <TabsTrigger value="update" className="text-xs" data-testid="tab-update">Update</TabsTrigger>
                                </TabsList>

                                {/* INFO TAB */}
                                <TabsContent value="info" className="mt-3 space-y-4" data-testid="tab-content-info">
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-muted-foreground">Package:</span><span className="ml-2">{selectedStudent.package_bought || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">Country:</span><span className="ml-2">{selectedStudent.country || 'N/A'}</span></div>
                                        <div><span className="text-muted-foreground">Classes:</span><span className="ml-2 font-mono">{selectedStudent.classes_attended || 0}</span></div>
                                        <div><span className="text-muted-foreground">CS Agent:</span><span className="ml-2">{selectedStudent.cs_agent_name || 'Unassigned'}</span></div>
                                        <div><span className="text-muted-foreground">Mentor:</span><span className="ml-2">{selectedStudent.mentor_name || 'Not Assigned'}</span></div>
                                        <div><span className="text-muted-foreground">Enrolled:</span><span className="ml-2">{formatDate(selectedStudent.created_at)}</span></div>
                                        <div><span className="text-muted-foreground">Enrollment Amt:</span><span className="ml-2 font-mono">AED {(selectedStudent.enrollment_amount || 0).toLocaleString()}</span></div>
                                        {selectedStudent.additional_phone && (
                                            <div><span className="text-muted-foreground">Alt Phone:</span><span className="ml-2">{selectedStudent.additional_phone}</span></div>
                                        )}
                                    </div>
                                    {/* Student Code */}
                                    <div className="flex items-center gap-3">
                                        <Label className="text-sm text-muted-foreground whitespace-nowrap">Student ID:</Label>
                                        <Input value={selectedStudent.student_code || ''} placeholder="Enter student code"
                                            className="h-8 text-sm font-mono max-w-[200px]" data-testid="student-code-input"
                                            onChange={(e) => setSelectedStudent(prev => ({ ...prev, student_code: e.target.value }))}
                                            onBlur={async (e) => { try { await apiClient.patch(`/students/${selectedStudent.id}/student-code`, { student_code: e.target.value }); } catch {} }}
                                        />
                                    </div>
                                    {/* Color Tags */}
                                    <div className="flex items-center gap-2 flex-wrap" data-testid="color-tag-picker">
                                        <span className="text-xs text-muted-foreground mr-1"><Palette className="h-3 w-3 inline mr-1" />Tag:</span>
                                        {COLOR_TAGS.map(tag => (
                                            <button key={tag.id}
                                                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border transition-all ${selectedStudent.color_tag === tag.id ? tag.color + ' ring-2 ring-offset-1 ring-current font-bold' : 'border-muted text-muted-foreground hover:border-foreground'}`}
                                                onClick={() => handleColorTag(selectedStudent, selectedStudent.color_tag === tag.id ? null : tag.id)}
                                                data-testid={`color-tag-btn-${tag.id}`}>
                                                <div className={`h-2 w-2 rounded-full ${tag.dot}`} />{tag.label}
                                            </button>
                                        ))}
                                    </div>
                                    {/* Upgrade Path */}
                                    {(selectedStudent.upgrade_history?.length > 0 || selectedStudent.current_course_name || selectedStudent.is_upgraded_student) && (
                                        <UpgradePathIndicator
                                            currentCourse={selectedStudent.current_course_name || selectedStudent.package_bought}
                                            upgradeHistory={selectedStudent.upgrade_history || []}
                                            showHistory={true}
                                        />
                                    )}
                                </TabsContent>

                                {/* NOTES TAB */}
                                <TabsContent value="notes" className="mt-3 space-y-3" data-testid="tab-content-notes">
                                    <div className="space-y-3">
                                        <div className="flex gap-2">
                                            <MentionTextarea value={newNote} onChange={setNewNote}
                                                placeholder="Add a note or comment. Use @name to mention a colleague (e.g. @ashwin)"
                                                rows={2} className="flex-1 text-sm" dataTestid="cs-note-input"
                                                onSubmit={handleAddNote} />
                                            <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim() || savingNote}
                                                className="self-end" data-testid="cs-save-note-btn">
                                                <Send className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground">
                                            Use <span className="font-mono bg-pink-500/10 text-pink-700 dark:text-pink-300 px-1 rounded">@handle</span> to tag a colleague — they&apos;ll get an in-app notification.
                                        </p>
                                        <ScrollArea className="max-h-[250px]">
                                            <div className="space-y-2">
                                                {studentNotes.map(n => (
                                                    <div key={n.id} data-note-id={n.id} className={`p-2.5 rounded text-sm border-l-3 transition-all ${
                                                        (n.created_by_role || '').includes('mentor') || (n.created_by_role || '').includes('master_of_academics')
                                                            ? 'bg-violet-50 dark:bg-violet-950/30 border-l-violet-500'
                                                            : (n.created_by_role || '').includes('cs')
                                                                ? 'bg-emerald-50 dark:bg-emerald-950/30 border-l-emerald-500'
                                                                : (n.created_by_role || '').includes('bd') || (n.created_by_role || '').includes('business')
                                                                    ? 'bg-amber-50 dark:bg-amber-950/30 border-l-amber-500'
                                                                    : 'bg-muted/50 border-l-gray-400'
                                                    }`}>
                                                        <RenderNoteBody text={n.text} />
                                                        <p className="text-[10px] text-muted-foreground mt-1.5 flex items-center gap-2">
                                                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                                                                {ROLE_LABELS[n.created_by_role] || n.created_by_role || 'Agent'}
                                                            </Badge>
                                                            <span className="font-medium">{n.created_by_name}</span>
                                                            <span>{new Date(n.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                                        </p>
                                                    </div>
                                                ))}
                                                {studentNotes.length === 0 && (
                                                    <p className="text-xs text-muted-foreground py-4 text-center">No notes yet. Add the first note above.</p>
                                                )}
                                            </div>
                                        </ScrollArea>
                                    </div>
                                </TabsContent>

                                {/* ACTIVITY TIMELINE TAB */}
                                <TabsContent value="activity" className="mt-3" data-testid="tab-content-activity">
                                    {activityLoading ? (
                                        <div className="flex items-center justify-center py-6 text-muted-foreground gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
                                    ) : (
                                        <ScrollArea className="max-h-[300px]">
                                            <div className="relative pl-4 space-y-0">
                                                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border" />
                                                {activityTimeline.map((item, idx) => {
                                                    const isNote = item.type === 'note';
                                                    const isUpgrade = item.action?.includes('upgrade');
                                                    const isStage = item.action?.includes('stage') || item.action?.includes('updated');
                                                    const isBD = item.action?.includes('bd_') || item.action?.includes('redeposit');
                                                    const dotColor = isNote ? 'bg-blue-500' : isUpgrade ? 'bg-violet-500' : isBD ? 'bg-amber-500' : isStage ? 'bg-emerald-500' : 'bg-gray-400';
                                                    const role = ROLE_LABELS[item.user_role] || item.user_role || '';
                                                    return (
                                                        <div key={item.id || idx} className="relative flex gap-3 pb-3">
                                                            <div className={`absolute left-[-13px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-background ${dotColor}`} />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs leading-relaxed">{item.description}</p>
                                                                <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                                                    {role && <span className="inline-flex px-1 py-0 rounded text-[9px] font-medium border bg-background">{role}</span>}
                                                                    <span className="font-medium">{item.user_name}</span>
                                                                    <span>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {activityTimeline.length === 0 && (
                                                    <p className="text-xs text-muted-foreground py-4 text-center">No activity recorded yet.</p>
                                                )}
                                            </div>
                                        </ScrollArea>
                                    )}
                                </TabsContent>

                                {/* TRANSACTIONS TAB */}
                                <TabsContent value="transactions" className="mt-3" data-testid="tab-content-transactions">
                                    <TransactionHistory studentId={selectedStudent.id} isSuperAdmin={isSuperAdmin} />
                                </TabsContent>

                                {/* CALLS TAB */}
                                <TabsContent value="calls" className="mt-3 space-y-3" data-testid="tab-content-calls">
                                    {selectedStudent.call_recording_url && (
                                        <div className="p-3 bg-muted/50 rounded-lg border">
                                            <Label className="text-xs text-muted-foreground">Latest Recording</Label>
                                            <a href={selectedStudent.call_recording_url} target="_blank" rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-primary hover:underline mt-1">
                                                Play Recording
                                            </a>
                                        </div>
                                    )}
                                    <CallRecordings phone={selectedStudent.phone} />
                                    <CallHistory contactId={selectedStudent.id} />
                                </TabsContent>

                                {/* UPDATE TAB */}
                                <TabsContent value="update" className="mt-3 space-y-4" data-testid="tab-content-update">
                                    {/* Editable Personal Info */}
                                    <div className="space-y-2">
                                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Personal Information</Label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <Label className="text-xs">Full Name</Label>
                                                <Input value={updateData.full_name || ''} placeholder="Full name"
                                                    onChange={(e) => setUpdateData({ ...updateData, full_name: e.target.value })}
                                                    className="h-8 text-sm" data-testid="edit-full-name" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Email</Label>
                                                <Input value={updateData.email || ''} placeholder="Email address" type="email"
                                                    onChange={(e) => setUpdateData({ ...updateData, email: e.target.value })}
                                                    className="h-8 text-sm" data-testid="edit-email" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Phone</Label>
                                                <Input value={updateData.phone || ''} placeholder="Phone number"
                                                    onChange={(e) => setUpdateData({ ...updateData, phone: e.target.value })}
                                                    className="h-8 text-sm" data-testid="edit-phone" />
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs">Additional Phone</Label>
                                                <Input value={updateData.additional_phone || ''} placeholder="Secondary number"
                                                    onChange={(e) => setUpdateData({ ...updateData, additional_phone: e.target.value })}
                                                    className="h-8 text-sm" data-testid="edit-additional-phone" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-t pt-3 grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Move to Stage</Label>
                                            <Select value={updateData.stage} onValueChange={(value) => setUpdateData({ ...updateData, stage: value })}>
                                                <SelectTrigger data-testid="cs-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
                                                <SelectContent>
                                                    {CS_STAGES.map((stage) => (
                                                        <SelectItem key={stage.id} value={stage.id}>{stage.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Classes Attended</Label>
                                            <Input type="number" min="0" value={updateData.classes_attended}
                                                onChange={(e) => setUpdateData({ ...updateData, classes_attended: parseInt(e.target.value) || 0 })}
                                                data-testid="classes-input" />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox id="onboarding" checked={updateData.onboarding_complete}
                                            onCheckedChange={(checked) => setUpdateData({ ...updateData, onboarding_complete: checked })}
                                            data-testid="onboarding-checkbox" />
                                        <Label htmlFor="onboarding">Onboarding Complete</Label>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Notes</Label>
                                        <Textarea value={updateData.notes} onChange={(e) => setUpdateData({ ...updateData, notes: e.target.value })}
                                            placeholder="Add notes..." rows={3} data-testid="cs-notes-input" />
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setShowDetailModal(false)}>Cancel</Button>
                                        <Button onClick={handleUpdateStudent} data-testid="update-student-btn">Update Student</Button>
                                    </DialogFooter>
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Reminder Modal */}
            <ReminderModal
                open={showReminderModal}
                onClose={() => { setShowReminderModal(false); setReminderStudent(null); }}
                entityType="student"
                entityId={reminderStudent?.id}
                entityName={reminderStudent?.full_name}
                onSuccess={handleReminderSuccess}
            />

            {/* Activation Questionnaire Modal */}
            <ActivationQuestionnaireModal
                open={showActivationModal}
                onClose={() => { setShowActivationModal(false); setPendingActivationStudent(null); }}
                student={pendingActivationStudent}
                onComplete={handleActivationComplete}
            />

            {/* Course & Addon Selection Modal (pitched_for_upgrade) — matches Sales CRM pattern */}
            <Dialog open={showPricingModal} onOpenChange={(o) => { if (!o) { setShowPricingModal(false); setPricingStudent(null); } }}>
                <DialogContent className="max-w-lg" data-testid="cs-course-selection-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-emerald-500" />
                            Pitch Upgrade — {pricingStudent?.full_name}
                        </DialogTitle>
                        <DialogDescription>
                            {pricingStudent?.student_code && <span className="font-mono text-xs mr-2">ID: {pricingStudent.student_code}</span>}
                            Select course & add-ons from the catalog
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {/* Current package info */}
                        <div className="flex items-center gap-3 text-sm p-3 rounded-lg bg-muted/50">
                            <span className="text-muted-foreground">Current Package:</span>
                            <Badge variant="outline">{pricingStudent?.current_course_name || pricingStudent?.package_bought || 'N/A'}</Badge>
                        </div>

                        {/* Course Selection */}
                        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-4">
                            <div className="flex items-center gap-2 text-amber-600">
                                <Target className="h-4 w-4" />
                                <span className="text-sm font-medium">Course & Add-ons Selection</span>
                            </div>

                            <div className="space-y-2">
                                <Label className="flex items-center gap-1.5">
                                    <Package className="h-3.5 w-3.5" />
                                    Select Course *
                                </Label>
                                <Select value={pricingCourseId} onValueChange={setPricingCourseId}>
                                    <SelectTrigger data-testid="cs-course-select">
                                        <SelectValue placeholder="Select course" />
                                    </SelectTrigger>
                                    <SelectContent position="popper" className="z-[9999]">
                                        {catalogCourses.map((course) => (
                                            <SelectItem key={course.id} value={course.id}>
                                                {course.name} — AED {course.price?.toLocaleString()}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Add-ons Selection */}
                            {catalogAddons.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-1.5">
                                        <ShoppingCart className="h-3.5 w-3.5" />
                                        Select Add-ons (Optional)
                                    </Label>
                                    <div className="grid grid-cols-2 gap-2 p-3 bg-background rounded-md border border-border">
                                        {catalogAddons.map((addon) => {
                                            const isSelected = pricingAddons.some(a => a.id === addon.id);
                                            return (
                                                <label
                                                    key={addon.id}
                                                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors text-sm ${
                                                        isSelected 
                                                            ? 'bg-primary/10 border border-primary/30' 
                                                            : 'hover:bg-muted/50 border border-transparent'
                                                    }`}
                                                    data-testid={`cs-addon-${addon.id}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setPricingAddons([...pricingAddons, { id: addon.id, name: addon.name, price: addon.price }]);
                                                            } else {
                                                                setPricingAddons(pricingAddons.filter(a => a.id !== addon.id));
                                                            }
                                                        }}
                                                        className="rounded border-gray-400 text-primary focus:ring-primary h-4 w-4"
                                                    />
                                                    <span className="flex-1 truncate">{addon.name.replace('Addons - ', '')}</span>
                                                    <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">AED {addon.price}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Price Breakdown */}
                            {pricingCourseId && (
                                <div className="p-3 bg-background rounded-md border border-border space-y-1.5" data-testid="cs-price-breakdown">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Course</span>
                                        <span>AED {(catalogCourses.find(c => c.id === pricingCourseId)?.price || 0).toLocaleString()}</span>
                                    </div>
                                    {pricingAddons.map(a => (
                                        <div key={a.id} className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">{a.name.replace('Addons - ', '')}</span>
                                            <span>AED {a.price?.toLocaleString()}</span>
                                        </div>
                                    ))}
                                    <div className="flex justify-between text-sm font-bold pt-2 border-t">
                                        <span>Total</span>
                                        <span className="text-primary">
                                            AED {((catalogCourses.find(c => c.id === pricingCourseId)?.price || 0) + pricingAddons.reduce((s, a) => s + (a.price || 0), 0)).toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowPricingModal(false); setPricingStudent(null); }}>Cancel</Button>
                        <Button
                            onClick={async () => {
                                if (!pricingCourseId || !pricingStudent) return;
                                setPricingSubmitting(true);
                                try {
                                    const course = catalogCourses.find(c => c.id === pricingCourseId);
                                    const totalPrice = (course?.price || 0) + pricingAddons.reduce((s, a) => s + (a.price || 0), 0);
                                    const res = await apiClient.post(`/cs/pitch-upgrade/${pricingStudent.id}`, {
                                        upgrade_path: course?.name || 'custom',
                                        selected_price: totalPrice,
                                        course_id: pricingCourseId,
                                        course_name: course?.name,
                                        selected_addons: pricingAddons,
                                    });
                                    toast.success(`Upgrade pitched: AED ${totalPrice.toLocaleString()}`);
                                    setShowPricingModal(false);
                                    setPricingStudent(null);
                                    fetchStudents();
                                } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to pitch upgrade');
                                } finally {
                                    setPricingSubmitting(false);
                                }
                            }}
                            disabled={!pricingCourseId || pricingSubmitting}
                            data-testid="confirm-pitch-btn"
                        >
                            {pricingSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Confirm Pitch — AED {pricingCourseId ? ((catalogCourses.find(c => c.id === pricingCourseId)?.price || 0) + pricingAddons.reduce((s, a) => s + (a.price || 0), 0)).toLocaleString() : '...'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upgrade Confirm + Payment Modal (upgraded) */}
            <UpgradeConfirmPaymentModal
                open={showConfirmModal}
                onClose={() => { setShowConfirmModal(false); setConfirmStudent(null); }}
                student={confirmStudent}
                onConfirmComplete={(updatedStudent) => {
                    setShowConfirmModal(false);
                    setConfirmStudent(null);
                    fetchStudents();
                    toast.success(`Upgrade confirmed for ${updatedStudent.full_name}. Student moved to New Student — please re-activate.`, { duration: 6000 });
                }}
            />

            {/* Student Merge Modal */}
            <MergeStudentModal
                open={showMergeModal}
                onClose={() => { setShowMergeModal(false); setMergeStudent(null); }}
                student={mergeStudent}
                onMergeSubmitted={() => {
                    setShowMergeModal(false);
                    setMergeStudent(null);
                    setShowDetailModal(false);
                    fetchStudents();
                }}
            />
        </div>
    );
};

export default CustomerServicePage;
